/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Avatar, AvatarGroup, Box, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';
import { SSOUtils } from 'application/Shared/Helpers';

/* Yarn */
import { uniq } from 'lodash';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

/*  Components  */
import { LightTooltip } from './Tooltips';

/**
 * Mapping of TenantApp source counter properties to their display label keys
 */
const discoveryPropertyLabels = Object.freeze({
    azureSignInCount: 'SSOSupportIcons.tooltip.discovered_by.microsoftEntraID',
    azureSignInDetected:
        'SSOSupportIcons.tooltip.discovered_by.microsoftEntraID',
    googleSignInCount: 'SSOSupportIcons.tooltip.discovered_by.google_workspace',
    googleSignInDetected:
        'SSOSupportIcons.tooltip.discovered_by.google_workspace',
    googleSSO: 'SSOSupportIcons.tooltip.discovered_by.browser_extension',
    googleSSODetected:
        'SSOSupportIcons.tooltip.discovered_by.browser_extension',
    microsoftSSO: 'SSOSupportIcons.tooltip.discovered_by.browser_extension',
    microsoftSSODetected:
        'SSOSupportIcons.tooltip.discovered_by.browser_extension'
});

/**
 * Mapping of SSO Provider to their matching TenantApp source counter properties
 */
const discoveryPropertiesByProvider = Object.freeze({
    [SSOUtils.PROVIDERS.google]: Object.freeze([
        'googleSSODetected', // browser extension first
        'googleSSO', // also browser extension
        'googleSignInDetected',
        'googleSignInCount'
    ]),
    [SSOUtils.PROVIDERS.microsoft]: Object.freeze([
        'microsoftSSODetected', // browser extension first
        'microsoftSSO', // also browser extension
        'azureSignInDetected',
        'azureSignInCount'
    ])
});

/**
 * Helper function to determine which discovery sources should be displayed for a given support object
 * @param {SSOSupport} support   The support object
 * @param {object}     tenantApp The TenantApp
 * @returns {string[]} List of labels to be displayed
 */
const getDiscoveredByLabels = (support, tenantApp) => {
    if (!support || !tenantApp) {
        return [];
    }

    const result = [];

    const properties = discoveryPropertiesByProvider[support.provider];
    if (properties) {
        properties.forEach((property) => {
            if (
                Object.prototype.hasOwnProperty.call(tenantApp, property) &&
                !!tenantApp[property] // want `true` or > 0, so just check if the value is truthy
            ) {
                result.push(discoveryPropertyLabels[property]);
            }
        });
    }

    return uniq(result);
};

/**
 * Renders an icon indicating the level of support for an SSO provider
 * @param {object} props
 * @param {SSOSupport} props.support The support object representing the level of support
 * @param {boolean} [standalone=false] Whether the icon is intended to be rendered as part of a list (false) or standalone (true)
 * @param {boolean} [large=false] Whether the icon should be rendered in a larger form
 */
const SSOSupportIcon = ({ support, standalone = false, large = false }) => {
    const theme = useTheme();
    if (!support.globalSupport && !support.tenantUse) {
        return null;
    }

    const iconSizeIceberg = large ? FontSizes[2] : FontSizes[1];
    const avatarSize = large ? '32px' : '24px';

    const variant = support.tenantUse ? 'avatarSecondary' : 'avatarTertiary';

    const iconPrintColor = support.tenantUse
        ? theme.palette.ssoSupport.tenantUse.iconPrint
        : theme.palette.ssoSupport.globalSupport.iconPrint;

    const backgroundColor = support.tenantUse
        ? theme.palette.ssoSupport.tenantUse.background
        : theme.palette.ssoSupport.globalSupport.background;

    return (
        <Box
            data-testid="ssoSupportAvatarWrapper"
            display="flex"
            alignItems="center"
            key={`app_support_${support.provider}`}
            style={{ background: 'none', border: 'none' }}
        >
            <Avatar
                sx={{
                    '@media print': { svg: { color: iconPrintColor } },
                    backgroundColor,
                    height: avatarSize,
                    marginRight: standalone ? 'inherit' : '-4px',
                    width: avatarSize
                }}
            >
                {support.provider === SSOUtils.PROVIDERS.google && (
                    <IconWithVariant
                        key={support.provider}
                        data-testid="faGoogleIcon"
                        variant={variant}
                        src="Google"
                        size={iconSizeIceberg}
                    />
                )}
                {support.provider === SSOUtils.PROVIDERS.microsoft && (
                    <IconWithVariant
                        key={support.provider}
                        data-testid="faMicrosoftIcon"
                        variant={variant}
                        src="Microsoft"
                        size={iconSizeIceberg}
                    />
                )}
            </Avatar>
        </Box>
    );
};

/**
 * Renders a tooltip which can give greater detail about the level of support for multiple SSO providers
 * @param {object}          props
 * @param {SSOSupport[]}    props.ssoSupport  This list of support for different providers
 * @param {React.ReactNode} props.children    The children nodes that this tooltip should wrap
 * @param {object}          [props.tenantApp] The TenantApp which can be used to enrich the tooltip with more information
 * @param {string}          [props.placement] Optional control over the desired placement of the tooltip
 */
const SSOSupportSourcesTooltip = ({
    children,
    ssoSupport,
    tenantApp,
    placement = 'right'
}) => {
    const { t } = useTranslation();

    const availableProviders = [];

    SSOUtils.DISPLAY_ORDER.forEach((provider) => {
        const support = ssoSupport.find((el) => el.provider === provider);
        if (!support) {
            return;
        }

        const discoveredByLabels = getDiscoveredByLabels(support, tenantApp);

        availableProviders.push(
            <Grid
                key={provider}
                container
                direction="row"
                className="available-provider"
                sx={{ paddingBottom: '12px', paddingTop: '12px' }}
            >
                <Box sx={{ display: 'block', height: '100%' }}>
                    <SSOSupportIcon support={support} standalone large />
                </Box>
                <Box sx={{ marginLeft: '8px' }}>
                    <Box sx={{ fontWeight: 600, marginBottom: '8px' }}>
                        {t(`SSOSupportIcons.sso_providers.${provider}`)}
                    </Box>
                    <Box>
                        <Box sx={{ fontWeight: 600 }}>
                            {t(
                                `SSOSupportIcons.tooltip.${
                                    support.tenantUse ? 'in_use' : 'not_in_use'
                                }`
                            )}
                        </Box>
                        {discoveredByLabels.length > 0 && (
                            <Box
                                sx={{ fontSize: FontSizes[0], fontWeight: 400 }}
                            >
                                {t('SSOSupportIcons.tooltip.discovered_by', {
                                    values: discoveredByLabels.map(t)
                                })}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Grid>
        );
    });

    return (
        <LightTooltip
            title={
                <Box
                    padding="8px"
                    sx={{
                        fontSize: FontSizes[2]
                    }}
                >
                    <Box sx={{ fontWeight: 600, padding: '8px' }}>
                        {t('SSOSupportIcons.tooltip.header')}
                    </Box>
                    <Box
                        sx={{
                            '> .available-provider:not(:first-of-type)': {
                                borderTopColor: 'ssoSupport.tooltip.divider',
                                borderTopStyle: 'solid',
                                borderTopWidth: '1px'
                            }
                        }}
                    >
                        {availableProviders}
                    </Box>
                </Box>
            }
            arrow
            placement={placement}
            slotProps={{
                tooltip: {
                    sx: {
                        minWidth: '400px'
                    }
                }
            }}
        >
            {children}
        </LightTooltip>
    );
};

/**
 * Displays an icon group indicating what SSO support is available for an App
 * @param {object}            props
 * @param {SSOSupport[]}      props.ssoSupport                 List of all providers and the level of support
 * @param {boolean}           [props.enableTooltip=false]      Whether to show a tooltip with more information on mouse hover
 * @param {object}            [props.tenantApp]                TenantApp data which can be used to enrich the tooltip with more information
 * @param {string}            [props.tooltipPlacement='right'] Optional control for the desired placement of the tooltip
 * @param {('center'|'left'|'flex-start')} [props.justify='center']         Determines how the icons should be horizontally justified
 * @returns
 */
export const SSOSupportIcons = ({
    ssoSupport,
    enableTooltip = false,
    tenantApp,
    tooltipPlacement = 'right',
    justify = 'left'
}) => {
    const { t } = useTranslation();

    if (!ssoSupport || ssoSupport.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: justify }}>
                {t('Common.unavailable')}
            </Box>
        );
    }

    const icons = [];

    SSOUtils.DISPLAY_ORDER.forEach((provider) => {
        const support = ssoSupport.find((el) => el.provider === provider);
        if (support) {
            icons.push(
                <SSOSupportIcon
                    key={support.provider}
                    support={support}
                    size={43}
                />
            );
        }
    });

    let result = (
        <AvatarGroup
            data-testid="ssoSupportAvatarGroup"
            sx={{
                alignItems: 'self-start',
                display: 'inline-flex'
            }}
        >
            {icons}
        </AvatarGroup>
    );

    if (enableTooltip) {
        result = (
            <SSOSupportSourcesTooltip
                ssoSupport={ssoSupport}
                tenantApp={tenantApp}
                placement={tooltipPlacement}
            >
                {result}
            </SSOSupportSourcesTooltip>
        );
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: justify, width: '100%' }}>
            {result}
        </Box>
    );
};
